import { RegionShared } from "@1games-ru/region-shared";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response.ts";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response.ts";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider.ts";

export default class CreateCharacterMpProvider {
  public static async getCreateCharacterConfig(): Promise<
    RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>
  > {
    return RpcMpProvider.getInstance().callServer<undefined, unknown>(
      RegionShared.Backend.Events.Character.GetConfig,
      undefined,
    );
  }

  public static async setDefault(): Promise<
    RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>
  > {
    return RpcMpProvider.getInstance().callClient<undefined, unknown>(
      RegionShared.Client.Events.Character.SetDefault,
      undefined,
    );
  }

  public static async setGender(
    gender: string,
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<{ gender: string }, unknown>(
      RegionShared.Client.Events.Character.SetGender,
      { gender },
    );
  }

  public static async setFace(
    index: number,
    value: number,
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<{ index: number; scale: number }, unknown>(
      RegionShared.Client.Events.Character.SetFace,
      { index, scale: value },
    );
  }

  public static async setHeadBlend(
    data: typeof RegionShared.Client.Requests.SetHeadBlendRequest,
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<
      typeof RegionShared.Client.Requests.SetHeadBlendRequest,
      typeof RegionShared.Client.Responses.SetHeadBlendResponse
    >(RegionShared.Client.Events.Character.SetHeadBlend, data);
  }

  public static async setGenetics(
    data: typeof RegionShared.Client.Requests.SetCustomizationRequest,
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<
      typeof RegionShared.Client.Requests.SetCustomizationRequest,
      typeof RegionShared.Client.Responses.SetCustomizationResponse
    >(RegionShared.Client.Events.Character.SetCustomization, data);
  }

  public static async setEyeColor(
    color: number,
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<{ color: number }, unknown>(
      RegionShared.Client.Events.Character.SetEyeColor,
      { color },
    );
  }

  public static async setHeadOverlay(data: {
    overlayId: number;
    index: number;
    opacity: number;
    firstColor: number;
    secondColor: number;
  }): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<
      {
        overlayId: number;
        index: number;
        opacity: number;
        firstColor: number;
        secondColor: number;
      },
      unknown
    >(RegionShared.Client.Events.Character.SetHeadOverlay, data);
  }

  public static async setHeadOverlayDefault(): Promise<
    RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>
  > {
    return RpcMpProvider.getInstance().callClient<undefined, unknown>(
      RegionShared.Client.Events.Character.SetHeadOverlayDefault,
      undefined,
      { noRet: true },
    );
  }

  public static async setClothes(data: {
    componentId: number;
    drawableId: number;
    textureId: number;
    paletteId: number;
  }): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<
      {
        componentId: number;
        drawableId: number;
        textureId: number;
        paletteId: number;
      },
      unknown
    >(RegionShared.Client.Events.Character.SetClothes, data);
  }

  public static async setHair(data: {
    drawableId: number;
  }): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<
      {
        drawableId: number;
      },
      unknown
    >(RegionShared.Client.Events.Character.SetHair, data);
  }

  public static async setHairColor(
    data: Partial<{
      color: number;
      highlightColor: number;
    }>,
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<
      Partial<{
        color: number;
        highlightColor: number;
      }>,
      unknown
    >(RegionShared.Client.Events.Character.SetHairColor, data);
  }

  public static async setPropVariant(data: {
    propId: number;
    drawableId: number;
    textureId: number;
  }): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<
      {
        propId: number;
        drawableId: number;
        textureId: number;
      },
      unknown
    >(RegionShared.Client.Events.Character.SetPropIndex, data);
  }

  public static async createCharacter(
    data: unknown,
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callServer<unknown, unknown>(
      RegionShared.Backend.Events.Character.CreateEnd,
      data,
    );
  }

  public static async setCamera(
    value: string,
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<{ camera: string }, unknown>(
      RegionShared.Client.Events.CreateCharacter.SetCamera,
      { camera: value },
    );
  }

  // TODO: нужно удалить и сделать глобально, если курсор направлен на CEF то отправлять ивент
  public static async setBrowserCefState(
    value: boolean,
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<boolean, unknown>(
      "", // RegionShared.Client.Events.CreateCharacter.SetBrowserCefState,
      value,
    );
  }

  public static async setCustomization(
    data: unknown,
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callClient<unknown, unknown>(
      RegionShared.Client.Events.Character.SetCustomization,
      data,
    );
  }
}
